/**
 * client.ts — OpenAI-compatible LLM API client.
 *
 * In dev (localhost): requests go through the Vite proxy at /api/llm
 *   which forwards to llm.core-loop.dev/v1 — no CORS issues.
 * In production (finance.core-loop.dev): direct fetch to llm.core-loop.dev/v1
 *   works because nginx now includes CORS headers for that origin.
 *
 * Configurable via environment variables:
 *   VITE_LLM_BASE_URL  — override the full base URL (optional)
 *   VITE_LLM_MODEL     — defaults to qwen2.5:14b
 *   VITE_LLM_API_KEY   — optional bearer token
 */

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant'
  content: string
}

// In dev, always use the Vite proxy path — VITE_LLM_BASE_URL is ignored in dev
// so the proxy can rewrite headers and avoid CORS.
// Override VITE_LLM_PROXY_TARGET in .env.local to point to a different Ollama host.
// In prod (served from finance.core-loop.dev), VITE_LLM_BASE_URL can override;
// defaults to the direct endpoint (nginx now allows that origin).
const IS_DEV = import.meta.env.DEV
const PROD_BASE = import.meta.env.VITE_LLM_BASE_URL ?? 'https://llm.core-loop.dev/v1'

const BASE_URL = IS_DEV ? '/api/llm' : PROD_BASE
const MODEL    = import.meta.env.VITE_LLM_MODEL    ?? 'qwen2.5:14b'
const API_KEY  = import.meta.env.VITE_LLM_API_KEY  ?? ''

export async function chatComplete(
  messages: ChatMessage[],
  onChunk: (delta: string) => void,
  signal?: AbortSignal,
): Promise<string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (API_KEY) headers['Authorization'] = `Bearer ${API_KEY}`

  const res = await fetch(`${BASE_URL}/chat/completions`, {
    method: 'POST',
    headers,
    signal,
    body: JSON.stringify({
      model: MODEL,
      messages,
      stream: true,
      temperature: 0.4,
    }),
  })

  if (!res.ok) {
    const err = await res.text().catch(() => res.statusText)
    throw new Error(`LLM API error ${res.status}: ${err}`)
  }

  if (!res.body) throw new Error('No response body from LLM API')

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let full = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    const chunk = decoder.decode(value, { stream: true })
    for (const line of chunk.split('\n')) {
      const trimmed = line.trim()
      if (!trimmed.startsWith('data: ')) continue
      const data = trimmed.slice(6)
      if (data === '[DONE]') continue
      try {
        const json = JSON.parse(data)
        const delta: string = json.choices?.[0]?.delta?.content ?? ''
        if (delta) {
          full += delta
          onChunk(delta)
        }
      } catch {
        // malformed SSE line — skip
      }
    }
  }

  return full
}
